import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { APIService } from 'src/api/api';

const isConfigurableColumn = (column) => column?.id && column.id !== 'action';

const normalizeHiddenColumns = (hiddenColumns, configurableColumns) => {
  const configurableColumnIds = new Set(configurableColumns.map((column) => column.id));
  return (Array.isArray(hiddenColumns) ? hiddenColumns : [])
    .map((columnId) => String(columnId))
    .filter((columnId, index, columns) => configurableColumnIds.has(columnId) && columns.indexOf(columnId) === index);
};

export const useTableColumnPreferences = (tableKey, columns, enabled = true) => {
  const configurableColumns = useMemo(() => columns.filter(isConfigurableColumn), [columns]);
  const configurableColumnKey = useMemo(
    () => configurableColumns.map((column) => column.id).join('|'),
    [configurableColumns]
  );
  const [hiddenColumnIds, setHiddenColumnIds] = useState([]);

  useEffect(() => {
    if (!enabled) return undefined;

    let isMounted = true;

    const loadPreference = async () => {
      const response = await APIService.getTableColumnPreference(tableKey);
      if (!isMounted || !response?.success) return;
      setHiddenColumnIds(normalizeHiddenColumns(response.result?.hidden_columns, configurableColumns));
    };

    loadPreference();

    return () => {
      isMounted = false;
    };
  }, [configurableColumnKey, configurableColumns, enabled, tableKey]);

  const saveHiddenColumns = useCallback((nextHiddenColumnIds) => {
    APIService.updateTableColumnPreference(tableKey, {
      hidden_columns: nextHiddenColumnIds
    });
  }, [tableKey]);

  const handleToggleColumn = useCallback((columnId) => {
    setHiddenColumnIds((currentHiddenColumnIds) => {
      const isHidden = currentHiddenColumnIds.includes(columnId);
      const nextHiddenColumnIds = isHidden
        ? currentHiddenColumnIds.filter((hiddenColumnId) => hiddenColumnId !== columnId)
        : [...currentHiddenColumnIds, columnId];
      saveHiddenColumns(nextHiddenColumnIds);
      return nextHiddenColumnIds;
    });
  }, [saveHiddenColumns]);

  const visibleColumns = useMemo(() => {
    const hiddenColumnSet = new Set(hiddenColumnIds);
    return columns.filter((column) => column.id === 'action' || !hiddenColumnSet.has(column.id));
  }, [columns, hiddenColumnIds]);

  return {
    configurableColumns,
    hiddenColumnIds,
    visibleColumns,
    onToggleColumn: handleToggleColumn
  };
};

const TableColumnVisibilityMenu = ({
  columns,
  hiddenColumnIds,
  onToggleColumn,
  buttonLabel,
  buttonSx,
  menuPaperSx
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const configurableColumns = useMemo(() => columns.filter(isConfigurableColumn), [columns]);
  const visibleConfigurableCount = configurableColumns.length - hiddenColumnIds.length;
  const isOpen = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Columns">
        <span>
          {buttonLabel ? (
            <Button
              aria-label="Columns"
              disabled={!configurableColumns.length}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              variant="outlined"
              size="small"
              startIcon={<ViewColumnIcon />}
              sx={buttonSx}
            >
              {buttonLabel}
            </Button>
          ) : (
            <IconButton
              aria-label="Columns"
              disabled={!configurableColumns.length}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              size="small"
            >
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          )}
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        keepMounted
        PaperProps={{ sx: menuPaperSx }}
      >
        {configurableColumns.map((column) => {
          const isHidden = hiddenColumnIds.includes(column.id);
          const isLastVisibleColumn = !isHidden && visibleConfigurableCount <= 1;
          return (
            <MenuItem
              dense
              disabled={isLastVisibleColumn}
              key={column.id}
              onClick={() => onToggleColumn(column.id)}
            >
              <Checkbox
                checked={!isHidden}
                size="small"
                sx={{
                  '&.Mui-checked': {
                    color: 'hsl(198 87% 32%)'
                  }
                }}
              />
              <ListItemText primary={column.name} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

TableColumnVisibilityMenu.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  hiddenColumnIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string,
  buttonSx: PropTypes.object,
  menuPaperSx: PropTypes.object
};

TableColumnVisibilityMenu.defaultProps = {
  buttonLabel: 'Columns',
  buttonSx: undefined,
  menuPaperSx: undefined
};

export default TableColumnVisibilityMenu;
